import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Footer from '../components/Footer';
import LeadForm from '../components/LeadForm'; // Import du formulaire

// Import des logos
import oradeaLogo from '../assets/logos/oradea.png';
import generaliLogo from '../assets/logos/generali.png';
import abeilleLogo from '../assets/logos/abeille.png';
import aprilLogo from '../assets/logos/april.png';
import regulatorsLogo from '../assets/logos/regulators.jpg';

const { FiCheckCircle, FiStar } = FiIcons;

const HomeRetarget = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-white font-sans text-[#004d44]">
      {/* Header */}
      <header className="bg-white shadow-sm py-4">
        <div className="max-w-6xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#00a48c] text-white flex items-center justify-center font-black">S</div>
            <span className="text-xl font-black tracking-tight">Simulation PER</span>
          </div>
          <div className="flex items-center gap-2 text-sm font-bold text-[#00a48c]">
            <SafeIcon icon={FiCheckCircle} />
            <span>Conseil Certifié & Gratuit</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
            
            {/* Left Column: The "Why" */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <h1 className="text-4xl md:text-5xl font-black text-[#004d44] mb-4 leading-tight tracking-tight uppercase">
                Finalisez Votre Simulation Personnalisée
              </h1>
              <p className="text-lg text-gray-600 font-medium mb-8">
                Découvrez le capital <strong className="text-[#00a48c]">exact</strong> que vous pouvez atteindre à la retraite et l'économie d'impôt <strong className="text-[#00a48c]">annuelle</strong> que vous pouvez réaliser.
              </p>

              <div className="bg-[#f0fcf9] p-6 rounded-2xl border border-[#e0f2ee] space-y-5 mb-8">
                <h3 className="font-bold text-center text-[#006b5a]">Nos clients sont souvent surpris de découvrir :</h3>
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiCheckCircle} className="text-[#00a48c] mt-1 shrink-0" />
                  <p className="text-sm text-gray-600 font-medium">Le montant réel de leur <strong className="font-bold">économie d'impôt</strong> chaque année.</p>
                </div>
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiCheckCircle} className="text-[#00a48c] mt-1 shrink-0" />
                  <p className="text-sm text-gray-600 font-medium">La puissance des <strong className="font-bold">intérêts composés</strong> sur leur capital final.</p>
                </div>
                 <div className="flex items-start gap-3">
                  <SafeIcon icon={FiCheckCircle} className="text-[#00a48c] mt-1 shrink-0" />
                  <p className="text-sm text-gray-600 font-medium">À quel point leur retraite peut être <strong className="font-bold">plus confortable</strong>.</p>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <p className="text-sm font-bold text-gray-500 mb-2">Rejoignez nos clients satisfaits :</p>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-500">Note Google</span>
                  <div className="flex text-yellow-400">
                    <SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" /><SafeIcon icon={FiStar} className="fill-current" />
                  </div>
                  <span className="font-bold text-gray-400 text-sm">(5.0 / 78 avis)</span>
                </div>
              </div>
            </motion.div>

            {/* Right Column: The "How" (The Form) */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}
              className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-gray-100"
            >
              <h2 className="text-2xl font-black text-center text-[#004d44] mb-2">Recevez votre simulation gratuite</h2>
              <p className="text-center text-sm text-gray-500 mb-6">Remplissez ces quelques champs pour continuer.</p>
              
              <LeadForm theme="cetelem-turquoise" />

              <p className="text-center text-xs text-gray-400 mt-4 font-medium">
                Un conseiller validera vos chiffres et vous enverra votre rapport détaillé par email.
              </p>
            </motion.div>

          </div>
        </div>
      </main>

      {/* Partners & Regulators Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h3 className="text-center text-sm font-bold text-gray-400 uppercase tracking-widest mb-8">
            En partenariat avec les leaders du marché
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[oradeaLogo, generaliLogo, abeilleLogo, aprilLogo].map((logo, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 flex justify-center items-center h-32 shadow-sm transition-shadow hover:shadow-md">
                <img src={logo} alt={`Partner logo ${index + 1}`} className="max-h-20 w-auto" />
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-col items-center">
            <h4 className="text-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">
              Un service régulé, sous le contrôle de l'ACPR et de l'ORIAS
            </h4>
            <div className="bg-white rounded-2xl p-6 inline-block shadow-sm">
                <img src={regulatorsLogo} alt="Logos ACPR Banque de France et Orias" className="h-14" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomeRetarget;